import { displayPortCode } from '@felix/domain';
import { Button, Modal, Skeleton, Textarea } from '@mantine/core';
import {
  IconArrowLeft,
  IconBan,
  IconLogin2,
  IconLogout2,
  IconPencil,
  IconPrinter,
} from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTimeInput } from '../../inputs/DateTimeInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { errorCode } from '../personnel/source.js';
import { useCountryName } from '../ports/catalog-context.js';
import { LoadError } from '../purchasing/table.js';
import { recordPdfFileName } from '../reports/record-pdf.js';
import { useRecordPrint } from '../reports/record-print.js';
import { fromLocalInput, toLocalInput } from '../voyage/NoonSections.js';
import { formatTzOffset } from '../voyage/noon-report.js';
import { localStamp, PortCallInfo, PortCallSummary, RelatedDocuments } from './PortCallParts.js';
import classes from './PortCalls.module.css';
import { portCallNumber, portCallPdf } from './port-call-pdf.js';
import { durationText, formToInput, valuesOf } from './port-calls.js';
import type { PortCallsSource } from './source.js';
import type { PortCall, PortCallDocument } from './types.js';

type Notice = { tone: 'success' | 'danger'; text: string };
type Dialog = 'arrival' | 'departure' | 'cancel' | null;

/**
 * Liman aramasının ayrıntısı (ADR 0052 §4): bilgiler, acente, faaliyetler ve not; sağda "Port Call
 * Summary" ve "Related Documents" (ortağın düzeni). Gemide "Edit", "Record Arrival", "Record
 * Departure" ve varıştan önce gerekçeli "Cancel Port Call"; ofiste salt okunur.
 */
export function PortCallDetail({
  source,
  id,
  message,
  onBack,
  onEdit,
}: {
  source: PortCallsSource;
  id: string;
  message: string | null;
  onBack: () => void;
  onEdit: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`portCalls.detail.${key}`, options);
  const error = (code: string) =>
    t(`portCalls.errors.${code}`, { defaultValue: t('portCalls.errors.GENERIC') });
  const countryName = useCountryName();
  const [call, setCall] = useState<PortCall | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [documents, setDocuments] = useState<PortCallDocument[] | null>(null);
  const [notice, setNotice] = useState<Notice | null>(
    message ? { tone: 'success', text: message } : null,
  );
  const [dialog, setDialog] = useState<Dialog>(null);
  const [busy, setBusy] = useState(false);
  const printer = useRecordPrint(source);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [next, docs] = await Promise.all([source.get(id), source.documents(id)]);
      setCall(next);
      setDocuments(docs);
    } catch (failure) {
      setLoadError(errorCode(failure));
    }
  }, [source, id]);
  useEffect(() => {
    void load();
  }, [load]);

  if (loadError) {
    return (
      <div className={classes.screen}>
        <PageHeader title={k('title')} crumb={k('title')} />
        <Panel>
          <LoadError title={k('error')} code={loadError} onRetry={() => void load()} />
        </Panel>
      </div>
    );
  }
  if (!call) {
    return (
      <div className={classes.screen} aria-busy="true">
        <PageHeader title={k('title')} crumb={k('title')} />
        <div className={classes.layout}>
          <Skeleton height={420} radius="md" />
          <Skeleton height={420} radius="md" />
        </div>
      </div>
    );
  }

  const editor = source.editor;
  const open = call.status !== 'cancelled';
  const run = async (work: () => Promise<PortCall>, done: string) => {
    setBusy(true);
    try {
      setCall(await work());
      setDialog(null);
      setNotice({ tone: 'success', text: done });
    } catch (failure) {
      setNotice({ tone: 'danger', text: error(errorCode(failure)) });
    } finally {
      setBusy(false);
    }
  };
  const record = (field: 'ata' | 'atd', at: string) => {
    if (!editor) return;
    const input = formToInput({ ...valuesOf(call), [field]: at });
    void run(
      () => editor.update(call.id, call.rowVersion, input),
      k(field === 'ata' ? 'arrivalSaved' : 'departureSaved'),
    );
  };
  const upload = async (file: File) => {
    if (!editor) return;
    setBusy(true);
    try {
      await editor.upload(call.id, file);
      setDocuments(await source.documents(call.id));
      setNotice({ tone: 'success', text: k('uploaded', { name: file.name }) });
    } catch (failure) {
      setNotice({ tone: 'danger', text: error(errorCode(failure)) });
    } finally {
      setBusy(false);
    }
  };

  /** Kaydın PDF'i (ADR 0062 §3): kayıtlı içerikten; web ve masaüstünde. */
  const print = async () => {
    const vessel = (await source.vessels().catch(() => [])).find((v) => v.id === call.vesselId);
    const failure = await printer.print((tr, brand) => ({
      definition: portCallPdf(tr, {
        call,
        vessel: { name: call.vesselName, imo: vessel?.imo ?? null },
        brand,
        printedAt: new Date().toISOString(),
        countryName: countryName(call.country),
      }),
      fileName: recordPdfFileName(portCallNumber(call), t('portCalls.detail.title')),
    }));
    if (failure) setNotice({ tone: 'danger', text: failure });
  };

  const unit = { day: t('portCalls.units.day'), hour: t('portCalls.units.hour') };
  const stop =
    call.itineraryRole === null
      ? call.voyageId && call.itineraryKey
        ? k('notInRoute')
        : k('extraCall')
      : t(`portCalls.form.roles.${call.itineraryRole}`);
  const facts: [string, string][] = [
    [t('portCalls.fields.vessel'), call.vesselName],
    [t('portCalls.fields.voyage'), call.voyageNo ?? k('noVoyage')],
    [t('portCalls.fields.routeStop'), stop],
    [
      t('portCalls.fields.port'),
      displayPortCode(call.portCode) ? `${call.portName} (${call.portCode})` : call.portName,
    ],
    [t('portCalls.fields.country'), countryName(call.country)],
    [t('portCalls.fields.locationType'), t(`portCalls.locationTypes.${call.locationType}`)],
    [t('portCalls.fields.berth'), call.berth ?? '—'],
    [t('portCalls.fields.timeZone'), formatTzOffset(call.tzOffsetMin)],
    [
      t('portCalls.fields.duration'),
      `${durationText(call.durationHours, unit)}${call.durationActual ? '' : ` (${t('portCalls.list.estimated')})`}`,
    ],
    [t('portCalls.fields.eta'), localStamp(call.eta, call.tzOffsetMin)],
    [t('portCalls.fields.etd'), localStamp(call.etd, call.tzOffsetMin)],
    [t('portCalls.fields.ata'), localStamp(call.ata, call.tzOffsetMin)],
    [t('portCalls.fields.atd'), localStamp(call.atd, call.tzOffsetMin)],
  ];
  const agent: [string, string | null][] = [
    [t('portCalls.fields.agentName'), call.agentName],
    [t('portCalls.fields.agentContact'), call.agentContact],
    [t('portCalls.fields.agentPhone'), call.agentPhone],
    [t('portCalls.fields.agentEmail'), call.agentEmail],
  ];

  return (
    <div className={classes.screen}>
      <PageHeader
        title={call.portName}
        crumb={call.portName}
        description={k('description', { vessel: call.vesselName })}
        actions={
          <div className={classes.headerActions}>
            <Button variant="default" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
              {k('back')}
            </Button>
            <Button
              variant="default"
              leftSection={<IconPrinter size={16} />}
              loading={printer.printing}
              onClick={() => void print()}
            >
              {t('recordPdf.print')}
            </Button>
            {editor && open ? (
              <>
                {call.status === 'planned' ? (
                  <Button
                    variant="light"
                    leftSection={<IconLogin2 size={16} />}
                    onClick={() => setDialog('arrival')}
                  >
                    {k('recordArrival')}
                  </Button>
                ) : null}
                {call.status === 'ongoing' ? (
                  <Button
                    variant="light"
                    leftSection={<IconLogout2 size={16} />}
                    onClick={() => setDialog('departure')}
                  >
                    {k('recordDeparture')}
                  </Button>
                ) : null}
                <Button leftSection={<IconPencil size={16} />} onClick={onEdit}>
                  {k('edit')}
                </Button>
                {call.status === 'planned' ? (
                  <Button
                    variant="light"
                    color="red"
                    leftSection={<IconBan size={16} />}
                    onClick={() => setDialog('cancel')}
                  >
                    {k('cancel')}
                  </Button>
                ) : null}
              </>
            ) : null}
          </div>
        }
      />
      {notice ? (
        <p
          className={classes.notice}
          data-tone={notice.tone}
          role={notice.tone === 'danger' ? 'alert' : 'status'}
        >
          {notice.text}
        </p>
      ) : null}
      {call.status === 'cancelled' ? (
        <p className={classes.notice} data-tone="warning">
          {k('cancelledNote', { reason: call.cancelReason ?? '' })}
        </p>
      ) : null}
      <div className={classes.layout}>
        <div className={classes.main}>
          <Panel title={k('details')}>
            <dl className={classes.facts}>
              {facts.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </Panel>
          <Panel title={k('agent')}>
            <dl className={classes.facts}>
              {agent.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value ?? '—'}</dd>
                </div>
              ))}
            </dl>
          </Panel>
          <Panel title={k('activities')}>
            <div className={classes.panelBody}>
              {call.activities.length === 0 ? (
                <p className={classes.note}>{k('noActivities')}</p>
              ) : (
                <span className={classes.chips}>
                  {call.activities.map((activity) => (
                    <span key={activity} className={classes.chip}>
                      {t(`portCalls.activities.${activity}`)}
                    </span>
                  ))}
                </span>
              )}
            </div>
          </Panel>
          <Panel title={k('notes')}>
            <div className={classes.panelBody}>
              <p className={call.notes ? classes.notesText : classes.note}>
                {call.notes ?? k('noNotes')}
              </p>
            </div>
          </Panel>
        </div>
        <aside className={classes.side} aria-label={k('side')}>
          <PortCallSummary data={call} />
          <PortCallInfo ship={source.scope === 'ship'} />
          <RelatedDocuments
            documents={documents}
            hrefOf={source.documentUrl?.bind(source)}
            onUpload={editor && open ? (file) => void upload(file) : undefined}
            busy={busy}
          />
        </aside>
      </div>
      {dialog === 'arrival' || dialog === 'departure' ? (
        <TimeDialog
          kind={dialog}
          tzOffsetMin={call.tzOffsetMin}
          busy={busy}
          onClose={() => setDialog(null)}
          onSave={(at) => record(dialog === 'arrival' ? 'ata' : 'atd', at)}
        />
      ) : null}
      {dialog === 'cancel' && editor ? (
        <CancelDialog
          busy={busy}
          onClose={() => setDialog(null)}
          onSave={(reason) =>
            void run(() => editor.cancel(call.id, call.rowVersion, reason), k('cancelled'))
          }
        />
      ) : null}
    </div>
  );
}

function TimeDialog({
  kind,
  tzOffsetMin,
  busy,
  onClose,
  onSave,
}: {
  kind: 'arrival' | 'departure';
  tzOffsetMin: number;
  busy: boolean;
  onClose: () => void;
  onSave: (at: string) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`portCalls.detail.${key}`);
  const [local, setLocal] = useState(() =>
    toLocalInput(new Date(Math.floor(Date.now() / 60_000) * 60_000).toISOString(), tzOffsetMin),
  );
  const at = local ? fromLocalInput(local, tzOffsetMin) : null;
  return (
    <Modal
      opened
      onClose={onClose}
      title={k(kind === 'arrival' ? 'recordArrival' : 'recordDeparture')}
      centered
    >
      <DateTimeInput
        label={`${t(kind === 'arrival' ? 'portCalls.fields.ata' : 'portCalls.fields.atd')} (${formatTzOffset(tzOffsetMin)})`}
        value={local}
        onChange={setLocal}
        withAsterisk
        description={at ? `= ${localStamp(at, 0)} UTC` : undefined}
      />
      <div className={classes.footer}>
        <Button variant="default" onClick={onClose}>
          {t('portCalls.actions.back')}
        </Button>
        <Button loading={busy} disabled={!at} onClick={() => at && onSave(at)}>
          {t('portCalls.actions.save')}
        </Button>
      </div>
    </Modal>
  );
}

function CancelDialog({
  busy,
  onClose,
  onSave,
}: {
  busy: boolean;
  onClose: () => void;
  onSave: (reason: string) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`portCalls.detail.${key}`);
  const [reason, setReason] = useState('');
  return (
    <Modal opened onClose={onClose} title={k('cancel')} centered>
      <p className={classes.note}>{k('cancelHint')}</p>
      <Textarea
        label={k('cancelReason')}
        withAsterisk
        autosize
        minRows={3}
        maxLength={500}
        value={reason}
        onChange={(event) => setReason(event.currentTarget.value)}
      />
      <div className={classes.footer}>
        <Button variant="default" onClick={onClose}>
          {t('portCalls.actions.back')}
        </Button>
        <Button
          color="red"
          loading={busy}
          disabled={reason.trim() === ''}
          onClick={() => onSave(reason.trim())}
        >
          {k('cancelConfirm')}
        </Button>
      </div>
    </Modal>
  );
}
